import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Badge, Drawer, Spin } from 'antd';
import {
  PlayCircleOutlined,
  RobotOutlined,
  ReadOutlined,
  FundOutlined,
  FolderOutlined,
  TrophyOutlined,
  SettingOutlined,
  CustomerServiceOutlined,
  LogoutOutlined,
  UserOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useMe } from '../../hooks/useMe';
import { usePortfolios } from '../../hooks/usePortfolios';
import { TOUCH_TARGET } from '../../constants/mobile';
import {
  useRegisterSupportEntry,
  useSupportPanel,
} from '../SupportWidget/SupportProvider';

/**
 * Everything the desktop sidebar carried that did not earn a tab: the secondary
 * destinations, the support entry and sign-out, plus the account block that used to
 * sit in the trading header's user pill.
 */
const MoreSheet: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { logout } = useAuth();
  const { openPanel, unread } = useSupportPanel();
  // Mounted with the tab bar, not with the open sheet: this is what tells the floating
  // support button there is another way in, so hiding it stays safe on mobile.
  useRegisterSupportEntry();

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  return (
    <Drawer
      placement="bottom"
      open={open}
      onClose={onClose}
      height="auto"
      title={null}
      closable={false}
      styles={{
        body: { padding: '8px 0 calc(8px + env(safe-area-inset-bottom))', background: '#1A1D23' },
        content: { borderRadius: '12px 12px 0 0', background: '#1A1D23' },
      }}
    >
      {open && <AccountBlock />}

      <Row
        icon={<FolderOutlined />}
        label="Portfolio"
        active={pathname === '/portfolio'}
        onClick={() => go('/portfolio')}
      />
      <Row
        icon={<TrophyOutlined />}
        label="Tournament"
        active={pathname === '/tournament'}
        onClick={() => go('/tournament')}
      />
      <Row
        icon={<FundOutlined />}
        label="Price Board"
        active={pathname === '/price-board'}
        onClick={() => go('/price-board')}
      />
      <Row
        icon={<PlayCircleOutlined />}
        label="Executions"
        active={pathname === '/executions'}
        onClick={() => go('/executions')}
      />
      <Row
        icon={<RobotOutlined />}
        label="AI Assistant"
        active={pathname === '/ai'}
        onClick={() => go('/ai')}
      />
      <Row
        icon={<ReadOutlined />}
        label="Docs"
        active={pathname === '/docs'}
        onClick={() => go('/docs')}
      />
      <Row
        icon={<SettingOutlined />}
        label="Settings"
        active={pathname === '/settings'}
        onClick={() => go('/settings')}
      />
      <Row
        icon={
          <Badge count={unread} size="small" offset={[4, -2]}>
            <CustomerServiceOutlined style={{ color: 'inherit' }} />
          </Badge>
        }
        label="Technical Support"
        onClick={() => {
          onClose();
          openPanel();
        }}
      />

      <div style={{ height: 1, background: '#2B3139', margin: '8px 16px' }} />

      <Row
        icon={<LogoutOutlined />}
        label="Logout"
        danger
        onClick={() => {
          onClose();
          logout();
          navigate('/');
        }}
      />
    </Drawer>
  );
};

/** Fetches only while the sheet is open — this sits behind a tab, not on every screen. */
const AccountBlock: React.FC = () => {
  const { user, loading } = useMe();
  const { portfolios, loading: loadingPortfolios } = usePortfolios();

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '4px 16px 14px',
        marginBottom: 6,
        borderBottom: '1px solid #2B3139',
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          flexShrink: 0,
          background: 'linear-gradient(135deg, #0ECB81 0%, #1E90FF 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <UserOutlined style={{ fontSize: 18, color: '#fff' }} />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        {loading ? (
          <Spin size="small" />
        ) : (
          <>
            <div
              style={{
                color: '#EAECEF',
                fontWeight: 600,
                fontSize: 14,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user?.name || 'User'}
            </div>
            <div
              style={{
                color: '#848E9C',
                fontSize: 12,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user?.email}
            </div>
          </>
        )}
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ color: '#848E9C', fontSize: 10 }}>Portfolios</div>
        <div style={{ color: '#EAECEF', fontSize: 16, fontWeight: 700 }}>
          {loadingPortfolios ? '—' : portfolios.length}
        </div>
      </div>
    </div>
  );
};

const Row: React.FC<{
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  danger?: boolean;
  onClick: () => void;
}> = ({ icon, label, active, danger, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      width: '100%',
      minHeight: TOUCH_TARGET + 4,
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      padding: '0 16px',
      background: active ? 'rgba(14, 203, 129, 0.08)' : 'transparent',
      border: 'none',
      cursor: 'pointer',
      color: danger ? '#F6465D' : active ? '#0ECB81' : '#EAECEF',
      fontSize: 15,
      textAlign: 'left',
      WebkitTapHighlightColor: 'transparent',
    }}
  >
    <span style={{ fontSize: 17, width: 20, display: 'inline-flex', justifyContent: 'center' }}>
      {icon}
    </span>
    <span style={{ flex: 1 }}>{label}</span>
    {!danger && <RightOutlined style={{ fontSize: 11, color: '#5E6673' }} />}
  </button>
);

export default MoreSheet;
